import React, { useState, useEffect } from 'react';
import { Box, Text, Button, Input, HStack, VStack, Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../contexts/ConfigContext';
import { uploadImageToOracleBucket, generateTemporaryPassword, generateUniqueId } from '../utils';
import { MAX_PHOTO_SIZE_BYTES, MAX_RETRY_ATTEMPTS, STEP_TRANSITION_DELAY_MS, MIN_PHONE_NUMBER_LENGTH, MIN_PHOTOS_REQUIRED, BUSINESS_CATEGORIES, normalizeCategory, ROUTES, API_ROUTES } from '../constants';
import SocialMediaAndPhotosStep from '../components/SocialMediaAndPhotosStep';
import CompletionScreen from '../components/CompletionScreen';
import BackButton from '../components/BackButton';
import PhoneInput from '../components/PhoneInput';
import LocationSelector from '../components/LocationSelector';

// Business categories - extract labels from shared constant
const businessCategories = BUSINESS_CATEGORIES.map(category => category.label);

const BusinessSignUpWizard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useConfig();

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3; // 3 actual steps, completion screen is separate

  // Step 1 - Contact Details
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [position, setPosition] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Get email from navigation state (passed from LoginPage)
  useEffect(() => {
    const state = location.state as { email?: string } | null;
    if (state?.email) {
      setEmail(state.email);
    }
  }, [location]);

  // Step 2 - Business Information
  const [businessCategory, setBusinessCategory] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');

  // Step 3 - Social Media & Photos
  const [instagramHandle, setInstagramHandle] = useState('');
  const [tiktokHandle, setTiktokHandle] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  // Email validation function
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone number validation function
  const isValidPhoneNumber = (phone: string) => {
    const phoneRegex = /^[0-9\s]+$/;
    return phoneRegex.test(phone) && phone.replace(/\s/g, '').length >= MIN_PHONE_NUMBER_LENGTH;
  };

  // Check if form is valid for current step
  const getStepValidation = (): boolean => {
    switch (currentStep) {
      case 1:
        return !!(firstName && lastName && email && validateEmail(email) && position && phoneNumber && isValidPhoneNumber(phoneNumber) && !isLoading);
      case 2:
        // Essential fields: category, name, country, city, address (all required by backend)
        return !!(businessCategory && businessName && country && city && businessAddress && businessAddress.trim() && !isLoading);
      case 3:
        return !!(uploadedPhotos.length >= MIN_PHOTOS_REQUIRED && !isLoading);
      default:
        return false;
    }
  };

  const isFormValid = getStepValidation();


  const handleNext = async () => {
    if (!isFormValid) return;

    setIsLoading(true);

    try {
      if (currentStep < totalSteps) {
        await new Promise(resolve => setTimeout(resolve, STEP_TRANSITION_DELAY_MS));
        setCurrentStep(currentStep + 1);
      } else {
        // Final step (step 3) - submit to API
        if (!config?.apiUrl) {
          throw new Error('API URL not configured');
        }

        // Validate all required fields before submission
        const requiredFields = {
          firstName: firstName?.trim(),
          lastName: lastName?.trim(),
          email: email?.trim(),
          position: position?.trim(),
          phoneNumber: phoneNumber?.replace(/\s/g, ''),
          businessCategory: businessCategory?.trim(),
          businessName: businessName?.trim(),
          country: country?.trim(),
          city: city?.trim(),
          businessAddress: businessAddress?.trim()
        };

        // Check for missing required fields
        const missingFields = Object.entries(requiredFields)
          .filter(([key, value]) => !value)
          .map(([key]) => key);

        if (missingFields.length > 0) {
          console.error('Missing required fields:', missingFields);
          throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }

        // Validate email format
        if (!validateEmail(email)) {
          throw new Error('Invalid email address');
        }

        // Validate phone number
        if (!isValidPhoneNumber(phoneNumber)) {
          throw new Error('Invalid phone number');
        }

        if (!config?.oracleBucket) {
          throw new Error('Oracle bucket configuration not found');
        }

        // Retry logic for ID conflicts
        const maxRetries = MAX_RETRY_ATTEMPTS;
        let retryCount = 0;
        let signupSuccess = false;

        while (retryCount < maxRetries && !signupSuccess) {
          try {
            // Generate unique ID on frontend
            const businessId = generateUniqueId('biz');
            console.log(`Attempt ${retryCount + 1}: Generated business ID: ${businessId}`);

            // Upload photos first using the generated ID
            let photoUrls: string[] = [];
            if (uploadedPhotos.length > 0) {
              try {
                photoUrls = await Promise.all(
                  uploadedPhotos.map(file => uploadImageToOracleBucket(file, businessId, config.oracleBucket!))
                );
                console.log('Photos uploaded successfully:', photoUrls.length, 'photos');
              } catch (photoError: any) {
                console.error('Error uploading photos:', photoError);
                throw new Error(`Failed to upload photos: ${photoError.message || 'Unknown error'}`);
              }
            }

            // Prepare API payload with generated ID and photo URLs
            const payload = {
              id: businessId, // Include generated ID in request
              firstName: requiredFields.firstName,
              lastName: requiredFields.lastName,
              email: requiredFields.email,
              position: requiredFields.position,
              phoneNumber: requiredFields.phoneNumber,
              // Normalize category to lowercase ID (e.g., "Restaurant" -> "restaurant")
              businessCategory: normalizeCategory(requiredFields.businessCategory),
              businessName: requiredFields.businessName,
              country: requiredFields.country?.toLowerCase(),
              city: requiredFields.city?.toLowerCase(),
              businessAddress: requiredFields.businessAddress,
              password: generateTemporaryPassword(), // Temporary password - user will need to reset
              instagramHandle: instagramHandle,
              tiktokHandle: tiktokHandle,
              websiteUrl: websiteUrl,
              photos: photoUrls // Include uploaded photo URLs in signup
            };

            // Submit to API to create user with generated ID and photos
            const response = await axios.post(`${config.apiUrl}${API_ROUTES.AUTH_BUSINESS_SIGNUP}`, payload);

            if (response.status === 201) {
              // Success - move to completion screen
              signupSuccess = true;
              setCurrentStep(4);
            } else {
              throw new Error(response.data?.error || 'Failed to create account');
            }
          } catch (error: any) {
            // Check if error is due to ID conflict
            if (error.response?.status === 409 || 
                error.response?.data?.error?.includes('already exists') ||
                error.response?.data?.error?.includes('ID already exists')) {
              retryCount++;
              if (retryCount >= maxRetries) {
                throw new Error('Failed to generate unique ID after multiple attempts. Please try again.');
              }
              console.log(`ID conflict detected, retrying with new ID (attempt ${retryCount + 1}/${maxRetries})...`);
              // Continue loop to retry with new ID
            } else {
              // Other errors should be thrown immediately
              throw error;
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Error saving form:', error);
      // Show error to user (you might want to add a toast/alert here)
      alert(error.response?.data?.error || error.message || 'Failed to submit form. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      // On step 1, navigate back to signup page
      navigate(ROUTES.HOME);
    }
  };


  return (
    <Box
      className="base-container"
      position="relative"
    >

      {/* Main Content */}
      <Box
        className="wizard-main-content"
      >
        {/* Progress indicator - positioned at top right */}
        {currentStep <= totalSteps && (
          <Box
            position="absolute"
            top="148px"
            right="20px"
            width="25px"
            height="32px"
            zIndex={10}
          >
            <Text
              className="subtitle-text"
            >
              {currentStep}/{totalSteps}
            </Text>
          </Box>
        )}

        {/* Back Arrow Button - Hide on completion screen */}
        {currentStep !== 4 && (
          <Box
            position="absolute"
            top="59px"
            left="22px"
            zIndex={10}
          >
            <BackButton onClick={handleBack} />
          </Box>
        )}

        {/* Title - "Sign up as Business" */}
        <Box
          position="absolute"
          top="61px"
          left="73px"
          width="292px"
          height="32px"
          zIndex={10}
        >
          <Text
            className="subtitle-text"            
          >
            {currentStep === 4 ? "" : "Sign up as a Business"}
          </Text>
        </Box>

        {/* Subtitle - changes based on step */}
        {currentStep <= totalSteps && (
          <Box
            position="absolute"
            top="147px"
            left="22px"
            width="300px"
            height="64px"
            zIndex={10}
          >
            <Text
              className="title-text"
            >
              {currentStep === 1 ? "Contact person details" :
               currentStep === 2 ? "Business information" :
               "Social profiles and photos"}
            </Text>
          </Box>
        )}

        {/* Scrollable Step Content Container */}
        <Box className="wizard-step-content">

          {/* Step 1: Contact Details */}
          {currentStep === 1 && (
            <>
              {/* Contact Details Form */}
              <Box
                top="0px"
                className='form-container'
              >              
              <VStack spacing="16px" align="stretch">
                {/* First Name */}
                <Box>
                  <Input
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="input-in-form"
                  />
                </Box>

                {/* Last Name */}
                <Box>

                  <Input
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="input-in-form"
                  />
                </Box>

                {/* Position */}
                <Box>

                  <Input
                    placeholder="Position"
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    className="input-in-form"
                  />
                </Box>

                {/* Contact Phone Number */}
                <Box>
                  <Text
                    className="subtitle-text"
                  >
                    Phone number
                  </Text>
                  <Box
                    borderBottom="1px solid #595C5D"
                    paddingBottom="15px"
                  >
                    <PhoneInput
                      value={phoneNumber}
                      onChange={setPhoneNumber}
                    />
                  </Box>
                </Box>
              </VStack>
            </Box>
          </>
          )}

          {/* Step 2: Business Information */}
          {currentStep === 2 && (
            <>
              {/* Business Category */}
              <Box
                top="0px"
                className="form-container"
              >
                  <Menu isLazy placement="bottom-start" closeOnBlur={false} strategy="fixed">
                    <MenuButton
                      as={Button}
                      className="menu-button"
                      rightIcon={<ChevronDownIcon color="#FFFFFF" boxSize="12px" />}                      
                      _expanded={{
                        borderBottom: "1px solid #C6D959"
                      }}
                    >
                      {businessCategory || "Select category"}
                    </MenuButton>
                    <MenuList
                      className="menu-list"
                    >
                      {businessCategories.map((category, index) => (
                        <MenuItem
                          key={index}
                          className="menu-item"
                          onClick={() => setBusinessCategory(category)}
                        >
                          <HStack spacing="8px" width="100%" justifyContent="space-between">
                            <Text>{category}</Text>
                            {businessCategory === category && (
                              <Box width="20px" height="20px" display="flex" alignItems="center" justifyContent="center">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="#C6D959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </Box>
                            )}
                          </HStack>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>

                </Box>

              {/* Business Name */}
              <Box
                top="95px"
                className="form-container"
              >
                  <Input
                    placeholder="Business name"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    className="input-in-form"
                  />
                </Box>

              {/* Location */}
              <Box
                top="215px"
                className="form-container"
              >
                  <Input
                    placeholder="Address"
                    value={businessAddress}
                    onChange={(e) => setBusinessAddress(e.target.value)}
                    className="input-in-form"
                    marginBottom="20px"
                    
                  />

                  <LocationSelector
                    selectedCountry={country}
                    selectedCity={city}
                    onCountryChange={setCountry}
                    onCityChange={setCity}
                  />
              </Box>
            </>
          )}

          {/* Step 3: Social Media & Photos */}
          {currentStep === 3 && (
            <SocialMediaAndPhotosStep
              instagramHandle={instagramHandle}
              setInstagramHandle={setInstagramHandle}
              tiktokHandle={tiktokHandle}
              setTiktokHandle={setTiktokHandle}
              websiteUrl={websiteUrl}
              setWebsiteUrl={setWebsiteUrl}
              uploadedPhotos={uploadedPhotos}
              setUploadedPhotos={setUploadedPhotos}
              photoUrls={photoUrls}
              setPhotoUrls={setPhotoUrls}
              maxPhotoSize={MAX_PHOTO_SIZE_BYTES}
              isFormValid={isFormValid}
              isLoading={isLoading}
              onNext={handleNext}
              buttonText="Complete"
              topPosition={0}
              showYouTube={false}
              showWebsite={true}
              showButton={false}
            />
          )}

          {/* Step 4: Completion Screen */}
          {currentStep === 4 && (
            <CompletionScreen
              title="Our team will review your business profile"
              subtitle="You will receive an email from us within the next 24 hours"              
            />
          )}
        </Box>
      </Box>

      {/* Fixed Next Button - Only show for steps 1-3 */}
      {currentStep <= totalSteps && (
        <Box
          position="absolute"
          bottom="30px"
          left="22px"
          width="332px"
          height="52px"
          zIndex={10}
        >
          <Button
            className="button-big"
            onClick={handleNext}
            isLoading={isLoading}
            loadingText="Loading..."
            isDisabled={!isFormValid}
          >
            {currentStep === 3 ? "Complete" : "Next"}
          </Button>
        </Box>
      )}


      {/* Progress Bar */}
      {currentStep <= totalSteps && (
        <Box className="wizard-progress-bar" >
          <Box
            className="wizard-progress-bar-steps"
            width={`${(currentStep / totalSteps) * 100}%`}
          />
        </Box>
      )}
    </Box>
  );
};

export default BusinessSignUpWizard;
